import React, { useEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import { StreamingTextRenderer } from "../StreamingTextRenderer";

export interface ThinkingBlockProps {
  /** The thinking content to display (plain text, no markdown). */
  content: string;
  /** When true, content is still streaming in. */
  isStreaming?: boolean;
  /** When set and not streaming, show "Thought for N seconds" indicator. */
  thoughtDurationSeconds?: number;
  className?: string;
}

export const ThinkingBlock = React.memo(
  ({
    content,
    isStreaming = false,
    thoughtDurationSeconds,
    className,
  }: ThinkingBlockProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!isStreaming) return;
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTo({
        top: el.scrollHeight - el.clientHeight,
        behavior: "smooth",
      });
    }, [content, isStreaming]);

    const fadeMask =
      "linear-gradient(to bottom, transparent 0, black 1.5rem, black calc(100% - 1.5rem), transparent 100%)";

    return (
      <div className={cn("text-sm text-muted-foreground", className)}>
        <div className="relative">
          <div
            ref={scrollRef}
            className="whitespace-pre-wrap break-words overflow-x-hidden overflow-y-auto max-h-[10rem] py-2"
            style={{
              WebkitMaskImage: fadeMask,
              maskImage: fadeMask,
            }}
          >
            {isStreaming && content ? (
              <StreamingTextRenderer>{content}</StreamingTextRenderer>
            ) : (
              content
            )}
          </div>
        </div>

        {!isStreaming && thoughtDurationSeconds != null && (
          <div className="text-xs text-muted-foreground/80">
            Thought for {thoughtDurationSeconds} second
            {thoughtDurationSeconds !== 1 ? "s" : ""}
          </div>
        )}
      </div>
    );
  },
);
